import React from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Users,
  Mountain,
  Building2,
  Calendar,
  Heart,
  Mail,
  ArrowRight,
  Compass,
  Camera,
  Globe,
  Star,
  Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEOHead from '../components/SEOHead';

const PartnershipsPage = () => {
  const partnershipTypes = [
    {
      icon: <MapPin className="h-8 w-8 text-white" />,
      title: "Tourism Boards & City Initiatives",
      description: "Promote walkable gems in your region. We co-create beautiful custom loops that highlight culture, nature, and small businesses."
    },
    {
      icon: <Mountain className="h-8 w-8 text-white" />,
      title: "Outdoor & Lifestyle Brands",
      description: "From boots to backpacks, we showcase the gear our community actually uses. Integrate into walk journals, gear reviews, and curated kits."
    },
    {
      icon: <Building2 className="h-8 w-8 text-white" />,
      title: "Local Businesses",
      description: "Want to be featured in a Food Walk? A Sunset Loop? We love spotlighting neighborhood cafés, bookstores, and boutiques that walkers adore."
    },
    {
      icon: <Calendar className="h-8 w-8 text-white" />,
      title: "Events & Experiences",
      description: "Collaborate on limited-edition guided walks, product activations, or multi-city walking campaigns with social amplification."
    },
    {
      icon: <Heart className="h-8 w-8 text-white" />,
      title: "Mission-Aligned Nonprofits",
      description: "We're proud to walk alongside causes that matter—urban greening, mobility access, mental health. Let's make steps count together."
    }
  ];

  const features = [
    {
      icon: <Target className="h-8 w-8 text-white" />,
      title: "Audience with purpose",
      description: "Our members walk for discovery, wellness, and wonder. They're deeply engaged and actively exploring."
    },
    {
      icon: <Globe className="h-8 w-8 text-white" />,
      title: "Hyper-local meets global",
      description: "From secret staircases in LA to sunset loops in Lisbon, our loops celebrate neighborhoods with style."
    },
    {
      icon: <Camera className="h-8 w-8 text-white" />,
      title: "Content that moves",
      description: "From high-quality photo journals to gear guides and cultural features, we create stories worth sharing."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Wanderloop Partnerships - Collaborate with Urban Walking Community"
        description="Partner with Wanderloop to reach passionate urban explorers. Tourism boards, brands, local businesses, and nonprofits - let's walk the world together."
        keywords="wanderloop partnerships, urban walking collaboration, tourism board partnerships, outdoor brand marketing, local business promotion"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-amber-400/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-400/10 rounded-full blur-xl"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-sm">
            <Users className="h-10 w-10 text-white" />
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
            Wanderloop
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
              Partnerships
            </span>
          </h1>

          <p className="text-2xl md:text-3xl mb-12 text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Let's walk the world together.
          </p>

          <div className="max-w-4xl mx-auto">
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              Wanderloop is building the most passionate walking community on the planet—connecting
              curious urban explorers, nature lovers, and local storytellers one loop at a time.
              We partner with brands, organizations, and creators who share our mission: to make
              walking more adventurous, accessible, and meaningful.
            </p>
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Why Partner with Wanderloop?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join a community that values authentic exploration, meaningful connections, and stories worth sharing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Community Stats */}
      <section className="py-20 bg-[#FFF8EE]" style={{ borderTop: '1px solid rgba(245,166,35,0.2)', borderBottom: '1px solid rgba(245,166,35,0.2)' }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-black text-gray-900 mb-2">Los Angeles</div>
              <div className="text-gray-600">Where we started</div>
            </div>
            <div>
              <div className="text-3xl font-black text-gray-900 mb-2">Growing Fast</div>
              <div className="text-gray-600">And expanding</div>
            </div>
            <div>
              <div className="text-3xl font-black text-gray-900 mb-2">New Walks Weekly</div>
              <div className="text-gray-600">Curated by locals</div>
            </div>
            <div>
              <div className="text-3xl font-black text-gray-900 mb-2">Open to Partners</div>
              <div className="text-gray-600">Let's build together</div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Opportunities */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Partnership Opportunities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From local businesses to global brands, we create meaningful collaborations that celebrate walking culture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnershipTypes.map((type, index) => (
              <Card key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 border-0 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                    {type.icon}
                  </div>
                  <CardTitle className="text-xl text-gray-800">{type.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">{type.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Break */}
      <section className="py-16 relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIzIi8+PC9nPjwvZz48L3N2Zz4=')]"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-6">
            Wanderloop is just getting started
          </h3>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
            — and we're looking for partners who want to grow with us.
          </p>
        </div>
      </section>

      {/* What a Partnership Looks Like */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">What a Partnership Looks Like</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We keep it simple. Here's what we can offer partners right now.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <MapPin className="h-6 w-6 text-amber-600" />,
                title: "Walk Features",
                description: "Your location, product, or experience featured in a curated walk — with photos, narrative, and a permanent place in our library."
              },
              {
                icon: <Star className="h-6 w-6 text-amber-600" />,
                title: "Gear Recommendations",
                description: "Honest, tested reviews in our gear section. We only feature products we'd actually bring on a walk."
              },
              {
                icon: <Users className="h-6 w-6 text-amber-600" />,
                title: "Community Access",
                description: "Reach our growing audience of engaged urban walkers — people who actively explore, share routes, and buy gear."
              },
              {
                icon: <Camera className="h-6 w-6 text-amber-600" />,
                title: "Content Creation",
                description: "Photo walks, route guides, gear journals — we create high-quality content that lives on Wanderloop and beyond."
              }
            ].map((item, index) => (
              <div key={index} className="flex gap-4 p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl">
                <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  {React.cloneElement(item.icon, { className: "h-6 w-6 text-white" })}
                </div>
                <div>
                  <h4 className="font-bold text-lg text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIzIi8+PC9nPjwvZz48L3N2Zz4=')]"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">Let's Talk</h2>
          <p className="text-xl md:text-2xl mb-12 text-blue-100 leading-relaxed">
            Have an idea or want to see our media kit?
          </p>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold">Email Us</div>
                  <a href="mailto:partnerships@wanderloop.app" className="text-blue-200 hover:text-white transition-colors">
                    partnerships@wanderloop.app
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Compass className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold">Based in</div>
                  <div className="text-blue-200">Los Angeles, walking everywhere</div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <a href="mailto:partnerships@wanderloop.app?subject=Media%20Kit%20Request&body=Hi%2C%20I'd%20like%20to%20receive%20the%20Wanderloop%20media%20kit.">
              <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold px-8 py-3 text-lg">
                <Mail className="mr-2 h-5 w-5" />
                Get Our Media Kit
              </Button>
            </a>

            <div className="text-center">
              <p className="text-sm text-blue-200 mb-4">
                Looking for Something Else?
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center text-sm">
                <Link to="/submit" className="text-blue-200 hover:text-white transition-colors flex items-center justify-center gap-1">
                  Submit a Walk <ArrowRight className="h-3 w-3" />
                </Link>
                <span className="hidden sm:inline text-blue-300">&bull;</span>
                <Link to="/contact" className="text-blue-200 hover:text-white transition-colors flex items-center justify-center gap-1">
                  Become an Ambassador <ArrowRight className="h-3 w-3" />
                </Link>
                <span className="hidden sm:inline text-blue-300">&bull;</span>
                <Link to="/membership" className="text-blue-200 hover:text-white transition-colors flex items-center justify-center gap-1">
                  See Wanderloop+ Membership <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Actions */}
      <FloatingActions />

      <Footer />
    </div>
  );
};

export default PartnershipsPage;
